import { useEffect, useRef, useState } from 'react';

// why: MaxLength(8000) on the render request bodies (Api repo) — longer text is not sent.
const MAX_BODY_LENGTH = 8000;
const DEBOUNCE_MS = 400;

interface UseSnippetPreviewOptions<TContext> {
  /** Raw text as typed; may contain `{Token}` placeholders. */
  body: string;
  /** Render context (customer, object, contact, dates …). Compared by value. */
  context: TContext;
  /** Resolves the body against the context — usually a render endpoint call. */
  render: (body: string, context: TContext) => Promise<string>;
  enabled?: boolean;
  /**
   * Bump to drop the last rendered text immediately (e.g. when a snippet is picked), so the
   * panel never shows the previous snippet's resolution under a new body.
   */
  resetKey?: unknown;
}

interface SnippetPreviewState {
  /** Whether a preview is worth showing (enabled, contains a `{`, within length). */
  active: boolean;
  /** Last resolved text; the raw body until the first render arrives. */
  text: string;
  loading: boolean;
}

/**
 * Debounced live preview for snippet text fields (ADR-0020). Re-renders 400 ms after the
 * last change of body or context, ignores out-of-order responses, keeps the previous text
 * visible while a request is pending and falls back to the raw body on errors.
 */
export function useSnippetPreview<TContext>({
  body,
  context,
  render,
  enabled = true,
  resetKey,
}: UseSnippetPreviewOptions<TContext>): SnippetPreviewState {
  const active = enabled && body.includes('{') && body.length <= MAX_BODY_LENGTH;
  // why: JSON identity — parents rebuild the context object every render, and the effect
  // must only fire when a value actually changed.
  const contextKey = JSON.stringify(context ?? null);

  const [rendered, setRendered] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // why: while typing, the previous resolution stays visible (no flicker); after a snippet
  // pick it would describe a different text, so the caller bumps resetKey to clear it.
  useEffect(() => {
    setRendered(null);
  }, [resetKey]);

  const renderRef = useRef(render);
  renderRef.current = render;
  const contextRef = useRef(context);
  contextRef.current = context;
  const sequenceRef = useRef(0);

  useEffect(() => {
    if (!active) {
      sequenceRef.current += 1;
      setLoading(false);
      return;
    }

    const sequence = ++sequenceRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await renderRef.current(body, contextRef.current);
        if (sequence === sequenceRef.current) setRendered(result);
      } catch (error) {
        console.error('[useSnippetPreview] render failed', error);
        if (sequence === sequenceRef.current) setRendered(body);
      } finally {
        if (sequence === sequenceRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      // why: invalidate the in-flight sequence so a late response cannot overwrite a newer one.
      if (sequence === sequenceRef.current) sequenceRef.current += 1;
    };
  }, [active, body, contextKey]);

  return {
    active,
    text: rendered ?? body,
    loading,
  };
}

export default useSnippetPreview;
